import { ellipsis } from "./ellipsis";
import { formatMessage } from "lib/globalize-util";
import { defaultEmptyValue } from "containers/folders/utils/constants";
import {
    isTrashView,
    removeAssortmentExtRegex,
    removeRollupExtRegex,
    removeShortcutExtRegex,
    removeWhiteboardExtRegex,
    SpaceView,
} from "containers/folders/hooks/utils";
import { IOrderBy, ItemKind, OrderByDirection, SpaceItemOrderByField } from "../types/base";
import moment from "moment";
import { serverRollup } from "../services/server-rollups";
import { CollectionType } from "../types/assortment";

export const getItemKindTranslated = (item: nuorder.folder.v1.Item, isInList: boolean = false) => {
    if (item?.kind === ItemKind.Folder) {
        if (isRollupItem(item.kind, item.name)) {
            return formatMessage("Assortment Rollup");
        }
        return formatMessage("Folder");
    } else if (item?.kind === ItemKind.CollectionDocument) {
        const isVerticalAssortmentItem = item?.collection_facet?.collection_type === CollectionType.Vertical;
        const isExternal = !!item?.collection_facet?.brand_portal_id;

        if (isVerticalAssortmentItem) {
            return formatMessage("Assortment (DTC)");
        }

        return isExternal ? formatMessage("Assortment (External)") : formatMessage("Assortment");
    } else if (item?.kind === ItemKind.BoardDocument) {
        return formatMessage("Whiteboard");
    } else if (item?.kind === ItemKind.Shortcut) {
        return isInList ? defaultEmptyValue : formatMessage("Shortcut");
    }
    return item?.kind;
};

export const getItemMerchantTranslated = (item: nuorder.folder.v1.Item) => {
    const merchantName = item?.owner?.merchant_name;

    return merchantName ?? "-";
};

// TODO update logic with filter info that will be provided by NU-45049
export const hasMoreThanOneUniqueMerchantName = (data: nuorder.folder.v1.Item[]) => {
    const merchantNames = new Set();
    data.forEach((item) => {
        if (item?.owner?.merchant_name) {
            merchantNames.add(item.owner.merchant_name);
        }
    });
    return merchantNames.size > 1;
};

export const checkIfDraggable = (item: nuorder.folder.v1.Item) =>
    isFolderItem(item?.kind) && !isRollupItem(item.kind, item.name);

export const isRollupItem = (itemKind, itemName: String = "") => {
    const [, rollupName] = itemName.match(removeRollupExtRegex) || [];
    return itemKind === ItemKind.Folder && rollupName;
};

export const isVerticalAssortmentItem = (itemKind, collectionType) => {
    return itemKind === ItemKind.CollectionDocument && collectionType === CollectionType.Vertical;
};

export const isFolderItem = (itemKind) => {
    return itemKind === ItemKind.Folder;
};

export const nameExtRemover = (name: string, kind: ItemKind) => {
    let nameWithoutExtension;
    switch (kind) {
        case ItemKind.CollectionDocument:
            const [, assortmentName] = name?.match(removeAssortmentExtRegex) || [];
            nameWithoutExtension = assortmentName || name;
            break;
        case ItemKind.BoardDocument:
            const [, whiteboardName] = name?.match(removeWhiteboardExtRegex) || [];
            nameWithoutExtension = whiteboardName || name;
            break;
        case ItemKind.Shortcut:
            const [, shortcutName] = name?.match(removeShortcutExtRegex) || [];
            nameWithoutExtension = shortcutName || name;
            break;
        case ItemKind.Folder:
            const [, rollupName] = name?.match(removeRollupExtRegex) || [];
            nameWithoutExtension = rollupName || name;
            break;
        default:
            nameWithoutExtension = name;
            break;
    }
    return nameWithoutExtension;
};

export const getItemIconName = (item: nuorder.folder.v1.Item) => {
    switch (item.kind) {
        case ItemKind.CollectionDocument:
            return "assortment24";
        case ItemKind.BoardDocument:
            return "whiteboard24";
        case ItemKind.Folder:
            if (isRollupItem(item.kind, item.name)) {
                return "rollup24";
            }
            return "folder24";
        case ItemKind.Shortcut:
            const shortcutKind = item.shortcut_facet?.target_item_kind;
            const targetItemName = item.shortcut_facet?.target_item_name;
            const [, rollupName] = targetItemName?.match(removeRollupExtRegex) || [];

            switch (shortcutKind) {
                case ItemKind.CollectionDocument:
                    return "shortcutAssortment24";
                case ItemKind.BoardDocument:
                    return "shortcutWhiteboard24";
                case ItemKind.Folder:
                    if (rollupName) {
                        return "shortcutRollup24";
                    }
                    return "shortcutFolder24";
                default:
                    return "shortcutFolder24";
            }
        default:
            return "folder24";
    }
};

export const getDefaultSortParams = (view: SpaceView): IOrderBy => {
    const isTrash = isTrashView(view);
    return {
        field: isTrash ? SpaceItemOrderByField.DeleteTime : SpaceItemOrderByField.Name,
        direction: isTrash ? OrderByDirection.Desc : OrderByDirection.Asc,
    };
};

export const generateShortcutName = ({
    item,
    includeMilliseconds,
    shouldIncludeDateFormat,
}: {
    item: Partial<nuorder.folder.v1.Item>;
    includeMilliseconds?: boolean;
    shouldIncludeDateFormat?: boolean;
}) => {
    const nameSections = [];
    const initialMaxLength = 94; // MAX_LENGTH_DATABASE(100) - "`.short` extension(6)";
    const name = nameExtRemover(item?.name || "", item?.kind as ItemKind);
    const prefixText = "Shortcut to ";
    const dateWithCustomFormat = moment().format(
        includeMilliseconds ? "YYYY-MMM-DD HH-mm-ss-SSS" : "YYYY-MMM-DD HH-mm"
    );

    if (shouldIncludeDateFormat) {
        nameSections.push(
            formatMessage(`${prefixText}|name|`, {
                name: ellipsis(name, {
                    char: "",
                    maxLength: initialMaxLength - (prefixText.length + (dateWithCustomFormat.length + 1)), // 1 is the value for the space between the target name and the date added by join(" ") in the return
                }),
            })
        );
        nameSections.push(dateWithCustomFormat);
    } else {
        nameSections.push(
            formatMessage(`${prefixText}|name|`, {
                name: ellipsis(name, {
                    char: "",
                    maxLength: initialMaxLength - prefixText.length,
                }),
            })
        );
    }

    return nameSections.filter((it) => !!it).join(" ");
};

export const isFolderOrFolderShortcutItem = (item: nuorder.folder.v1.Item) => {
    return (
        (item.kind === ItemKind.Shortcut && item.shortcut_facet.target_item_kind === ItemKind.Folder) ||
        item.kind === ItemKind.Folder
    );
};

const getFolderUrl = (itemId: string): string => {
    const urlParamsObj = new URLSearchParams(window.location.search);
    urlParamsObj.set("itemId", itemId);

    return `/drive?${urlParamsObj.toString()}`;
};

export const getFolderUrlWithView = (item: nuorder.folder.v1.Item, view: SpaceView) => {
    const urlParamsObj = new URLSearchParams(window.location.search);
    urlParamsObj.set("itemId", item.id);
    urlParamsObj.set("view", view);
    return `/drive?${urlParamsObj.toString()}`;
};

export const getDriveItemUrl = async (merchantId: string, item: nuorder.folder.v1.Item) => {
    let url = "";
    switch (item.kind) {
        case ItemKind.CollectionDocument: {
            url = `/assortment/${item.document_id}`;
            break;
        }
        case ItemKind.BoardDocument: {
            url = `/b/whiteboard/${item.document_id}`;
            break;
        }
        case ItemKind.Folder:
            if (isRollupItem(item.kind, item.name)) {
                const snapShot = await serverRollup.getLastSnapshot(item.id, merchantId);
                url = `/assortment/${snapShot.id}`;
            } else {
                url = getFolderUrl(item.id);
            }
            break;
        case ItemKind.Shortcut:
            const shortcutKind = item.shortcut_facet?.target_item_kind;
            const targetItemName = item.shortcut_facet?.target_item_name;
            const targetItemId = item.shortcut_facet?.target_item_id;
            const targetDocumentId = item.shortcut_facet?.target_document_id;

            switch (shortcutKind) {
                case ItemKind.CollectionDocument:
                    url = `/assortment/${targetDocumentId}`;
                    break;
                case ItemKind.BoardDocument:
                    url = `/b/whiteboard/${targetDocumentId}`;
                    break;
                case ItemKind.Folder:
                    if (isRollupItem(ItemKind.Folder, targetItemName)) {
                        const snapShot = await serverRollup.getLastSnapshot(targetItemId, merchantId);
                        url = `/assortment/${snapShot.id}`;
                    } else {
                        url = getFolderUrl(targetItemId);
                    }
            }
    }
    return url;
};

export const transferShortcutToCollection = (items: nuorder.folder.v1.Item[]) => {
    return items.map((item) => {
        return {
            ...item,
            document_id: item.shortcut_facet?.target_document_id,
            name: item.shortcut_facet?.target_item_name,
            kind: item.shortcut_facet?.target_item_kind,
            shortcut_id: item.id,
            id: item.shortcut_facet?.target_item_id,
        };
    });
};

export const getPermissionName = (item: nuorder.folder.v1.Item) => {
    let title;

    const isReaderPermission = !item.capabilities.can_comment && !item.capabilities.can_edit;
    const isWriterPermission =
        item.capabilities.can_rename && item.capabilities.can_edit && !item.capabilities.can_trash;
    const isFileOrganizerPermission = item.capabilities.can_trash && !item.capabilities.can_share;
    const isOrganizerPermission = item.capabilities.can_share && !item.capabilities.can_untrash;

    if (isReaderPermission) {
        title = formatMessage("Reader");
    } else if (isWriterPermission) {
        title = formatMessage("Writer");
    } else if (isFileOrganizerPermission) {
        title = formatMessage("File Organizer");
    } else if (isOrganizerPermission) {
        title = formatMessage("Organizer");
    }
    return title;
};
